/**
 * Middleware de autenticación administrativa
 * Proporciona verificaciones adicionales de seguridad para acceso administrativo
 */

import React from 'react';

import { getUser } from '../services/users';
import { User } from '../types/auth';

export interface AdminAuthResult {
  isAuthorized: boolean;
  user: User | null;
  error?: string;
  redirectTo?: string;
}

/**
 * Verifica si un usuario tiene permisos administrativos válidos
 * @param user Usuario actual del contexto de autenticación
 * @returns Resultado de la verificación con detalles
 */
export const verifyAdminAccess = async (user: User | null): Promise<AdminAuthResult> => {
  // 1. Verificar que hay un usuario autenticado
  if (!user) {
    return {
      isAuthorized: false,
      user: null,
      error: 'Usuario no autenticado',
      redirectTo: '/auth/login'
    };
  }

  // 2. Verificar rol básico
  if (user.role !== 'admin') {
    return {
      isAuthorized: false,
      user,
      error: `Acceso denegado. Rol actual: ${user.role}, requerido: admin`,
      redirectTo: '/dashboard'
    };
  }

  // 3. Verificar que el usuario sigue siendo válido en Firestore
  try {
    const currentUserData = await getUser(user.uid);
    
    if (!currentUserData) {
      return {
        isAuthorized: false,
        user,
        error: 'Usuario no encontrado en la base de datos',
        redirectTo: '/auth/login'
      };
    }

    // 4. Verificar que el rol no ha cambiado
    if (currentUserData.role !== 'admin') {
      return {
        isAuthorized: false,
        user,
        error: 'Los permisos administrativos han sido revocados',
        redirectTo: '/dashboard'
      };
    }

    // 5. Verificar que la cuenta está activa
    if ((currentUserData as { isActive?: boolean }).isActive === false) {
      return {
        isAuthorized: false,
        user,
        error: 'Cuenta administrativa desactivada',
        redirectTo: '/auth/login'
      };
    }

    // 6. Todo está correcto
    return {
      isAuthorized: true,
      user: {
        ...user,
        role: currentUserData.role, // Asegurar que el rol está actualizado
      }
    };

  } catch (error) {
    console.error('Error verificando acceso administrativo:', error);
    return {
      isAuthorized: false,
      user,
      error: 'Error verificando permisos administrativos',
      redirectTo: '/dashboard'
    };
  }
};

/**
 * Hook personalizado para verificar acceso administrativo en componentes
 * @param user Usuario actual
 * @returns Estado de autorización y funciones de verificación
 */
export const useAdminAuth = (user: User | null) => {
  const [authResult, setAuthResult] = React.useState<AdminAuthResult>({
    isAuthorized: false,
    user: null
  });
  const [isVerifying, setIsVerifying] = React.useState(true);

  React.useEffect(() => {
    const verifyAccess = async () => {
      setIsVerifying(true);
      const result = await verifyAdminAccess(user);
      setAuthResult(result);
      setIsVerifying(false);
    };

    verifyAccess();
  }, [user]);

  return {
    isAuthorized: authResult.isAuthorized,
    isVerifying,
    error: authResult.error,
    redirectTo: authResult.redirectTo,
    verifiedUser: authResult.user
  };
};

/**
 * Verificaciones específicas para diferentes niveles de acceso administrativo
 */
export const AdminPermissions = {
  /**
   * Verificar si puede gestionar usuarios
   */
  canManageUsers: (user: User | null): boolean => {
    return user?.role === 'admin';
  },

  /**
   * Verificar si puede gestionar WebIDs
   */
  canManageWebIDs: (user: User | null): boolean => {
    return user?.role === 'admin';
  },

  /**
   * Verificar si puede ver analytics
   */
  canViewAnalytics: (user: User | null): boolean => {
    return user?.role === 'admin';
  },

  /**
   * Verificar si puede gestionar cupones
   */
  canManageCoupons: (user: User | null): boolean => {
    return user?.role === 'admin';
  },

  /**
   * Verificar si puede modificar configuración del sistema
   */
  canModifySystemSettings: (user: User | null): boolean => {
    return user?.role === 'admin';
  },

  /**
   * Verificar si puede eliminar usuarios (acción crítica)
   */
  canDeleteUsers: (user: User | null): boolean => {
    return user?.role === 'admin';
  },

  /**
   * Verificar si puede cambiar roles de otros usuarios
   */
  canChangeUserRoles: (user: User | null, targetUserId: string): boolean => {
    // No puede cambiar su propio rol
    if (user?.uid === targetUserId) {
      return false;
    }
    return user?.role === 'admin';
  }
};

/**
 * Logging de acciones administrativas para auditoría
 */
export const logAdminAction = async (
  adminUser: User,
  action: string,
  targetResource: string,
  details?: Record<string, unknown>
) => {
  try {
    const logEntry = {
      adminUid: adminUser.uid,
      adminEmail: adminUser.email,
      action,
      targetResource,
      details: details || {},
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      ip: 'client-side' // En producción, obtener del servidor
    };

    console.log('🔍 Admin Action:', logEntry);
    
    // En producción, enviar a Firestore collection 'admin_audit_logs'
    // await addDoc(collection(db, 'admin_audit_logs'), logEntry);
    
  } catch (error) {
    console.error('Error logging admin action:', error);
  }
};


